import { PropTypes } from 'prop-types';
import React, { Component } from 'react';
import { IoCameraOutline, IoCloudUploadOutline, IoImagesOutline } from 'react-icons/io5';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FileType, getUploadFileTypeKey } from '../../views/claimDetail/models';
import { hideDialog, showDialog } from '../actions';
import * as utils from '../utils';
import FileReader from './FileReader';
import ModalDialog from './Modal';

// Re-read base64 per file in selection order. react-file-reader fills
// its own base64[] in load-completion order, so indexes don't match fileList.
const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.FileReader) {
      reject(new Error('FileReader unsupported'));
      return;
    }
    const reader = new window.FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

class UploadSection extends Component {
  static propTypes = {
    fileType: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
      .isRequired,
    addFile: PropTypes.func.isRequired,
    showDialog: PropTypes.func.isRequired,
    hideDialog: PropTypes.func.isRequired,
    title: PropTypes.string,
    buttonLabel: PropTypes.string,
    multipleFiles: PropTypes.bool,
    enableCameraCapture: PropTypes.bool,
  };

  state = { isPhotoSourceOpen: false };

  cameraInputRef = React.createRef();

  uploadInputRef = React.createRef();

  handleFiles = async (files) => {
    const list = files && files.fileList;
    if (!list || list.length === 0) return;
    const accepted = Array.isArray(this.props.fileType)
      ? this.props.fileType
      : [this.props.fileType];

    if (!this.props.multipleFiles) {
      const first = list[0];
      const mimePrefix = first && first.type ? getUploadFileTypeKey(first.type) : '';
      if (accepted.includes(mimePrefix) && FileType[mimePrefix] !== undefined) {
        this.openUploadModal(files, FileType[mimePrefix]);
      }
      return;
    }

    const items = [];
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      if (!f || !f.type) continue;
      const mimePrefix = getUploadFileTypeKey(f.type);
      if (!(accepted.includes(mimePrefix) && FileType[mimePrefix] !== undefined)) continue;
      let base64;
      try {
        base64 = await readFileAsDataUrl(f);
      } catch (err) {
        continue;
      }
      items.push({
        file: { base64, name: f.name, type: f.type },
        fileType: FileType[mimePrefix],
      });
    }
    this.processQueue(items);
  };

  handleNativeFiles = async (event) => {
    const fileList = Array.from(event.target.files || []);
    event.target.value = '';
    if (!fileList.length) return;

    let base64;
    if (!this.props.multipleFiles) {
      try {
        base64 = await readFileAsDataUrl(fileList[0]);
      } catch (err) {
        return;
      }
    }
    this.handleFiles({ fileList, base64 });
  };

  selectPhotoSource = (inputRef) => {
    this.setState({ isPhotoSourceOpen: false });
    inputRef.current?.click();
  };

  // hideDialog between iterations forces UpsertFileDialog to remount so
  // its internal `submitting` state doesn't carry over to the next file.
  processQueue = (queue) => {
    if (!queue || queue.length === 0) return;
    const [head, ...rest] = queue;
    const onSubmit = (payload) => {
      this.handleFileUpload(payload);
      this.props.hideDialog();
      if (rest.length > 0) {
        setTimeout(() => this.processQueue(rest), 0);
      }
    };
    this.props.showDialog('UPSERT_FILE', {
      onSubmit,
      file: head.file,
      fileType: head.fileType,
    });
  };

  handleFileUpload = ({ file, description }) => {
    const fd = new FormData();

    const toBlob = (dataUrl, mimeType) => {
      if (!dataUrl) return null;
      const base64String = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      try {
        const byteChars = atob(base64String);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType || 'application/octet-stream' });
      } catch (e) {
        return null;
      }
    };

    let fileArg = file;
    if (!(fileArg instanceof Blob)) {
      const name = (fileArg && fileArg.name) || 'upload';
      const type = (fileArg && fileArg.type) || 'application/octet-stream';
      const blob = toBlob(fileArg && fileArg.base64, type);
      if (blob) {
        try {
          fileArg = new File([blob], name, { type });
        } catch (e) {
          blob.name = name;
          fileArg = blob;
        }
      }
    }

    // Sanitize filename to avoid invalid header characters (e.g., for x-amz-meta-originalname)
    const sanitize = (name) => {
      if (!name) return 'upload';
      // Remove CR/LF and non-printable, replace anything not safe with '_'
      return (
        name
          .replace(/[\r\n]+/g, '')
          .replace(/[^A-Za-z0-9._ -]+/g, '_')
          .trim()
          .substring(0, 200) || 'upload'
      );
    };
    const safeName = sanitize(fileArg && fileArg.name);

    fd.append('file', fileArg, safeName);
    fd.append('description', description);

    this.props.addFile(fd);
  };

  openUploadModal = (file, fileType) => {
    // Sanitized snapshot only; File objects aren't serializable in Redux.
    const first = file && file.fileList && file.fileList[0];
    const safeFile = first
      ? {
          base64: file.base64,
          name: first.name,
          type: first.type,
        }
      : null;

    this.props.showDialog('UPSERT_FILE', {
      onSubmit: this.handleFileUpload,
      file: safeFile,
      fileType,
    });
  };

  render() {
    if (this.props.enableCameraCapture && this.props.fileType === 'image') {
      return (
        <div className="upload-section claim-photo-upload">
          <button
            type="button"
            className="button claim-photo-add-button"
            onClick={() => this.setState({ isPhotoSourceOpen: true })}
          >
            <IoImagesOutline aria-hidden="true" />
            <span>Add Photos</span>
          </button>
          <input
            ref={this.cameraInputRef}
            className="claim-photo-source-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={this.handleNativeFiles}
          />
          <input
            ref={this.uploadInputRef}
            className="claim-photo-source-input"
            type="file"
            accept="image/*"
            multiple={this.props.multipleFiles === true}
            onChange={this.handleNativeFiles}
          />
          <ModalDialog
            title="Add Photos"
            isOpen={this.state.isPhotoSourceOpen}
            close={() => this.setState({ isPhotoSourceOpen: false })}
            showFooter={false}
          >
            <div className="claim-photo-source-actions">
              <button
                type="button"
                className="button primary mobile-camera-option"
                onClick={() => this.selectPhotoSource(this.cameraInputRef)}
              >
                <IoCameraOutline aria-hidden="true" />
                <span>Take Photo</span>
              </button>
              <button
                type="button"
                className="button"
                onClick={() => this.selectPhotoSource(this.uploadInputRef)}
              >
                <IoCloudUploadOutline aria-hidden="true" />
                <span>Upload Photos</span>
              </button>
              <button
                type="button"
                className="button"
                onClick={() => this.setState({ isPhotoSourceOpen: false })}
              >
                Cancel
              </button>
            </div>
          </ModalDialog>
        </div>
      );
    }

    return (
      <div className="upload-section">
        <FileReader
          handleFiles={this.handleFiles}
          fileType={this.props.fileType}
          title={this.props.title || 'Upload'}
          buttonLabel={this.props.buttonLabel}
          multipleFiles={this.props.multipleFiles === true}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      showDialog,
      hideDialog,
    },
    dispatch
  );
};

export default connect(null, mapDispatchToProps)(UploadSection);
